import { useState, useEffect } from "react";
import { getUserRoles, createUser } from "../../services/panel/panelServices";
import toast from "../../utils/toast";

const EMPTY_FORM = {
  roleId: "",
  name: "",
  email: "",
  mobile: "",
  whatsapp: "",
  pincode: "",
  address: "",
};

export default function CreateUserModal({ open, onClose, onCreated }) {
  const [token, setToken] = useState("");
  const [isRoleData, setRoleData] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fresh token each time the modal opens
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setErrors({});
      fetchRoles();
    }
  }, [open]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const res = await getUserRoles();

      if (res?.statuscode === 1) {
        setToken(res?.data?.token || "");
        setRoleData(res?.data?.roles || []);
      } else {
        toast.error(res?.msg || "Failed to load roles.");
      }
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to fetch roles", "error");
    } finally {
      setRolesLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.roleId) e.roleId = "Select a role.";
    if (!form.name.trim()) e.name = "Name is required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      e.email = "Enter a valid email.";
    if (!/^\d{10}$/.test(form.mobile.trim()))
      e.mobile = "Enter a 10-digit mobile number.";
    if (form.whatsapp.trim() && !/^\d{10}$/.test(form.whatsapp.trim()))
      e.whatsapp = "Enter a 10-digit number.";
    if (!/^\d{6}$/.test(form.pincode.trim()))
      e.pincode = "Enter a 6-digit pincode.";
    if (!form.address.trim()) e.address = "Address is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!token) {
      toast.error("Session token missing. Please reopen the form.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        Token: token,
        RoleID: Number(form.roleId),
        Name: form.name.trim(),
        EmailID: form.email.trim(),
        MobileNo: form.mobile.trim(),
        WhatsAppNumber: form.whatsapp.trim(),
        Pincode: form.pincode.trim(),
        Address: form.address.trim(),
      };
      const res = await createUser(payload);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "User created.");
        onCreated?.();
        onClose();
      } else {
        toast.error(res?.msg || "Failed to create user.");
        fetchRoles(); // token may be single-use
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const errorText = (key) =>
    errors[key] ? (
      <span style={{ color: "#dc2626", fontSize: 11 }}>{errors[key]}</span>
    ) : null;
  const errorBorder = (key) => (errors[key] ? { borderColor: "#dc2626" } : {});

  return (
    <div
      className={`modal-overlay ${open ? "open" : ""}`}
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="modal-box" style={{ width: 560 }}>
        <h3>Add user</h3>
        <p>Create a Checker or Maker login for this corporate account</p>
        <div className="form-grid">
          <div className="field">
            <label>Role</label>
            <select
              name="roleId"
              value={form.roleId}
              onChange={handleChange}
              disabled={rolesLoading}
              style={errorBorder("roleId")}
            >
              <option value="">
                {rolesLoading ? "Loading roles..." : "Select role"}
              </option>
              {isRoleData.map((r) => (
                <option key={r.roleID} value={r.roleID}>
                  {r.roleName}
                </option>
              ))}
            </select>
            {errorText("roleId")}
          </div>
          <div className="field">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Anjali Mehta"
              style={errorBorder("name")}
            />
            {errorText("name")}
          </div>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="e.g. anjali@vermatextiles.in"
              style={errorBorder("email")}
            />
            {errorText("email")}
          </div>
          <div className="field">
            <label>Mobile number</label>
            <input
              type="text"
              name="mobile"
              maxLength={10}
              value={form.mobile}
              onChange={handleChange}
              placeholder="10-digit mobile"
              style={errorBorder("mobile")}
            />
            {errorText("mobile")}
          </div>
          <div className="field">
            <label>WhatsApp number (optional)</label>
            <input
              type="text"
              name="whatsapp"
              maxLength={10}
              value={form.whatsapp}
              onChange={handleChange}
              placeholder="10-digit number"
              style={errorBorder("whatsapp")}
            />
            {errorText("whatsapp")}
          </div>
          <div className="field">
            <label>Pincode</label>
            <input
              type="text"
              name="pincode"
              maxLength={6}
              value={form.pincode}
              onChange={handleChange}
              placeholder="e.g. 400001"
              style={errorBorder("pincode")}
            />
            {errorText("pincode")}
          </div>
        </div>
        <div className="field">
          <label>Address</label>
          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="e.g. Mumbai"
            style={errorBorder("address")}
          />
          {errorText("address")}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Creating..." : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}
